import { CircleHelp } from 'lucide-react';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  /** Optionales Mini-Label oberhalb des Titels, z. B. "Themen ▸ Mobilität" oder "Dataset". */
  eyebrow?: ReactNode;
  /** Pflicht: Haupt-Titel der Seite (h1). */
  title: ReactNode;
  /** Optional: Erklaerung der Seite; erscheint hinter dem Fragezeichen neben dem Titel. */
  lead?: ReactNode;
  /** Optional: sichtbarer Absatz unter dem Titel (Inhalt, z. B. Beschreibung eines Datensatzes, oder ein Hinweis). */
  summary?: ReactNode;
  /** Optional: Slot rechts oben (Buttons, Suche etc.). Mobile: rutscht unter den Titel. */
  actions?: ReactNode;
  /** Optional: Badge-Reihe unter dem Titel (Lifecycle, Access-Rights, HVD-Tags etc.). */
  meta?: ReactNode;
  /** Zusaetzliche Klassen am Wrapper. */
  className?: string;
}

/**
 * Ebene des Kopfes: `page` (h1, Seitenkopf) oder `section` (h2, ohne Trennlinie) — fuer Seiten,
 * die als Reiter in einer Sammelseite stecken (z. B. Zugangsdaten und MQTT unter „Zugänge“).
 * Die Sammelseite setzt die Ebene per Provider, die eingebettete Seite bleibt unveraendert.
 */
export const PageHeaderLevel = createContext<'page' | 'section'>('page');

/**
 * Vereinheitlichter Page-Header.
 *
 * Stufe-2-MR-B fuehrt diese Komponente ein, um die Ad-hoc-Header in allen
 * Pages zu ersetzen. Slot-basiertes Layout, mobile-tauglich (Stack), klare
 * Trennung zum Page-Inhalt via Bottom-Border.
 *
 * Visual:
 * - Eyebrow: kleines Uppercase-Label, tracking-wider, muted-foreground.
 * - Title:   h1, font-display, text-2xl md:text-3xl, bold, tracking-tight (wie die Uebersicht).
 * - Lead:    Erklaerung hinter einem Fragezeichen neben dem Titel (Maus, Fokus oder Tippen).
 * - Summary: sichtbarer Absatz, max-w-prose, muted-foreground.
 * - Actions: rechts oben (Desktop) bzw. unten (Mobile).
 * - Meta:    Reihe von Badges unter dem Titel.
 * - Abstand: mb-8, ohne Trennlinie; die Pfadzeile in der Kopfleiste ordnet die Seite ein.
 */
const present = (n: ReactNode): boolean => n !== undefined && n !== null && n !== '';

/**
 * Fragezeichen neben dem Titel: Erklaerung beim Ueberfahren mit der Maus oder per Tastatur-Fokus,
 * per Klick bzw. Tippen auf- und zuklappbar (Touch). Der Text bleibt fuer Screenreader am Knopf.
 */
function HelpHint({ children, title }: { children: ReactNode; title: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={typeof title === 'string' ? `Erklärung zu „${title}“` : 'Erklärung zu dieser Seite'}
            onClick={(e) => {
              e.preventDefault();
              setOpen((o) => !o);
            }}
            className="inline-flex shrink-0 items-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="seite-erklaerung"
          >
            <CircleHelp className="h-4 w-4" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="max-w-md border bg-popover px-3 py-2 text-sm font-normal leading-relaxed text-popover-foreground shadow-md"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function PageHeader({
  eyebrow,
  title,
  lead,
  summary,
  actions,
  meta,
  className,
}: PageHeaderProps): JSX.Element {
  const section = useContext(PageHeaderLevel) === 'section';
  const help = present(lead) ? <HelpHint title={title}>{lead}</HelpHint> : null;
  const visible = present(summary) ? <p className="max-w-prose text-sm text-muted-foreground">{summary}</p> : null;
  if (section) {
    return (
      <header className={cn('mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between', className)}>
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
            {help}
          </div>
          {meta !== undefined && meta !== null ? <div className="flex flex-wrap items-center gap-2">{meta}</div> : null}
          {visible}
        </div>
        {actions !== undefined && actions !== null ? (
          <div className="flex flex-wrap items-center gap-2 md:shrink-0">{actions}</div>
        ) : null}
      </header>
    );
  }
  return (
    <header
      className={cn(
        'mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between',
        className,
      )}
    >
      <div className="min-w-0 space-y-2">
        {eyebrow !== undefined && eyebrow !== null && eyebrow !== '' ? (
          <div className="text-sm font-medium text-muted-foreground">
            {eyebrow}
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
            {title}
          </h1>
          {help}
        </div>
        {meta !== undefined && meta !== null ? (
          <div className="flex flex-wrap items-center gap-2">{meta}</div>
        ) : null}
        {visible}
      </div>
      {actions !== undefined && actions !== null ? (
        <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:pt-1">
          {actions}
        </div>
      ) : null}
    </header>
  );
}
